import { useEffect, useRef } from 'react'
import {
    GameUIMessageType,
    InfoLineUpdateFreespinCountMessage,
    SetSpeedButtonLevelMessage,
    FeatureStatusChangedMessage,
    FeatureAutostartCounterEndedMessage,
    SplashStatusChangeRequestedMessage,
} from 'game-ui-types'
import { useUltraPlayStore } from '@stores/ultraPlayStore'
import { useConfigurationStore } from '@stores'
import { createUltraPlaySpinRecorder } from './ultraPlaySpinRecorder'

// Owns all Ultra Play <-> engine message syncing, extracted from App.tsx so the feature is
// self-contained. Attaches its own window 'message' listener and delegates bet recording / feature
// lifecycle to the spin recorder; the forwarding cases (speed re-assert, intro/splash skips) just
// keep the engine moving while Ultra Play plays through a feature. The listener is only attached
// when the Ultra Play feature flag is enabled.
export const useUltraPlayEngineSync = () => {
    const recorderRef = useRef<ReturnType<typeof createUltraPlaySpinRecorder> | null>(null)
    const recorder = (recorderRef.current ??= createUltraPlaySpinRecorder())
    const status = useUltraPlayStore((s) => s.status)
    const hasUltraPlay = useConfigurationStore((s) => s.hasUltraPlay)

    // Drop any pending bet when a session ends (→ READY) so the next session starts clean.
    useEffect(() => {
        if (status === 'READY') recorder.resetPending()
    }, [status, recorder])

    useEffect(() => {
        if (!hasUltraPlay) return

        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData?.type

            switch (type) {
                // Unified feature lifecycle for Ultra Play (started / completed).
                case GameUIMessageType.FEATURE_STATUS_CHANGED: {
                    recorder.onFeatureStatusChanged((rawData as FeatureStatusChangedMessage).payload)

                    break
                }
                case GameUIMessageType.INFO_LINE_UPDATE_FREESPIN_COUNT: {
                    recorder.onFreespinCount(rawData as InfoLineUpdateFreespinCountMessage)

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_UPDATE_AUTOPLAY_COUNT: {
                    recorder.onAutoplayCount((rawData as { payload?: number }).payload)

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_AUTOPLAY_STOPPED: {
                    recorder.onAutoplayStopped()

                    break
                }
                case GameUIMessageType.SET_SPEED_BUTTON_LEVEL: {
                    // The engine drops the speed to the first level when a feature starts. During Ultra
                    // Play features we want to stay at max speed — re-assert it.
                    const up = useUltraPlayStore.getState()

                    if (!up.isInFeature) break

                    const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2
                    const { speedLevel } = rawData as SetSpeedButtonLevelMessage

                    if (speedLevel < maxSpeedLevel) {
                        window.postMessage(
                            {
                                type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL,
                                speedLevel: maxSpeedLevel,
                            } as SetSpeedButtonLevelMessage,
                            '*'
                        )
                    }

                    break
                }
                case GameUIMessageType.FEATURE_AUTOSTART_COUNTER_STARTED: {
                    // Free spins start screen began — end its counter so Ultra Play doesn't wait on it.
                    // Play-through only (PLAYING); when PAUSED the Special Feature dialog is up.
                    if (useUltraPlayStore.getState().status === 'PLAYING') {
                        window.postMessage(
                            {
                                type: GameUIMessageType.FEATURE_AUTOSTART_COUNTER_ENDED,
                                payload: { feature: 'freespins' },
                            } as FeatureAutostartCounterEndedMessage,
                            '*'
                        )
                    }

                    break
                }
                case GameUIMessageType.FREESPINS_SPLASH_STARTED: {
                    // A free spins splash (intro / retrigger / outro) began — complete it so Ultra Play
                    // skips the animation. Play-through only (PLAYING).
                    if (useUltraPlayStore.getState().status === 'PLAYING') {
                        window.postMessage(
                            {
                                type: GameUIMessageType.SPLASH_STATUS_CHANGE_REQUESTED,
                                payload: { status: 'completed' },
                            } as SplashStatusChangeRequestedMessage,
                            '*'
                        )
                    }

                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [recorder, hasUltraPlay])
}
